<template>
  <BottomSheet ref="sheet" v-model:open="open" :on-closed="onClosed">
    <template #title>{{ entry.name }}</template>
    <template v-if="entry.note" #meta>{{ entry.note }}</template>

    <div ref="form" class="form">
      <div class="row">
        <AppField
          v-model="quantity"
          class="quantity"
          :label="t('item.quantity')"
          kind="decimal"
          :error="errors.quantity"
          data-field="quantity"
          :autofocus="focusQuantity && writeInto !== null"
          @blur="leaveField('quantity')"
        />
        <SegmentedControl v-model="unit" :legend="t('item.unit')" :options="units" />
      </div>

      <AppField
        v-model="amount"
        :label="t('item.price')"
        kind="decimal"
        :error="errors.amount"
        data-field="amount"
        @blur="leaveField('amount')"
      >
        <template #suffix>
          <CurrencySelect v-model="currency" :label="t('item.currency')" />
        </template>
      </AppField>

      <!-- «Тут дешевле» lives in the box of the figure it compares (MOL-92, В-6). The sheet stands
           on the bottom of the screen, so a line coming in lifts everything above it: it grows over
           frames, never in one (MOL-151), and follows typing only once it pauses (adversarial Е). -->
      <div class="per-unit-box">
        <div class="per-unit">
          <span class="per-unit-label">{{ t('item.unit_price_label') }}</span>
          <span class="per-unit-value">{{ perUnit ?? '—' }}</span>
        </div>
        <AppReveal>
          <!-- Keyed by its words: a line that says something else goes and comes rather than changing
               height in one frame — a second line of it lifted the price by 17 px (adversarial Е′). -->
          <p
            v-if="itemHint"
            :key="itemHint.text"
            class="hint"
            :class="`hint-${itemHint.kind}`"
            data-hint="item"
          >
            <component :is="itemHint.icon" class="hint-icon" aria-hidden="true" />
            <span>{{ itemHint.text }}</span>
          </p>
        </AppReveal>
        <AppReveal>
          <p
            v-if="alternativeHint"
            :key="alternativeHint"
            class="hint hint-alternative"
            data-hint="alternative"
          >
            <IconSwapHorizontal class="hint-icon" aria-hidden="true" />
            <span>{{ alternativeHint }}</span>
          </p>
        </AppReveal>
      </div>
      <p v-if="!perUnit" class="caption">{{ t('item.unit_price_pending') }}</p>
      <!-- The price of the package needs no quantity: a weight not yet known still has its
           price in roubles (adversarial A6). -->
      <p v-if="converted" class="caption">
        <span>{{ t('money.converted_package', { amount: converted }) }}</span>
        <span class="note">{{ t('money.converted_note') }}</span>
      </p>

      <!-- The item came by its code (MOL-100, В-1): whoever holds the package may say it is not
           this one. Under the form, not beside «Записать», and asked before it is done (review Ж):
           the code goes for everyone, with no «Вернуть». -->
      <template v-if="code">
        <!-- A group named by its question: the focus waits on «Отменить», and a screen reader reads
             the question with it — the app's live region is outside the modal sheet. -->
        <div v-if="confirming" class="detach" role="group" :aria-labelledby="detachQuestionId">
          <p :id="detachQuestionId" class="detach-question">
            {{ t('item.barcode.detach_question', { code, name: entry.name }) }}
          </p>
          <p class="caption">{{ t('item.barcode.detach_hint') }}</p>
          <!-- Destructive for everyone and with no «Вернуть»: not the filled button, and the focus
               waits on «Отменить» (review Л). Side by side where both fit, as 4h draws them (MOL-212):
               each as wide as its wider word, so «Отвязываем…» is never broken inside (adversarial А1). -->
          <div class="detach-actions">
            <AppButton
              variant="danger-ghost"
              :busy="detaching"
              :busy-label="t('item.barcode.detach_busy')"
              @click="notThis"
            >
              {{ t('item.barcode.detach') }}
            </AppButton>
            <AppButton ref="detachCancel" variant="ghost" :inactive="detaching" @click="keepCode">
              {{ t('item.cancel') }}
            </AppButton>
          </div>
          <p v-if="detachFailed" class="caption offline" role="alert">
            {{ t('item.barcode.not_this_failed') }}
          </p>
        </div>
        <AppButton v-else ref="notThisButton" variant="ghost" block @click="askDetach">
          <template #icon><IconBarcodeScan /></template>
          {{ t('item.barcode.not_this', { code }) }}
        </AppButton>
      </template>
    </div>

    <template #footer>
      <template v-if="writeInto">
        <AppButton ref="saveButton" size="large" block @click="submit">
          {{ editing ? t('item.save_edit') : t('item.save') }}
        </AppButton>
        <AppButton v-if="removable" ref="removeButton" variant="danger-ghost" block @click="remove">
          {{ t('item.delete') }}
        </AppButton>
        <p v-if="!online" class="caption offline">{{ t('item.offline_note') }}</p>
      </template>
      <template v-else>
        <p class="no-trip">{{ t('item.no_trip.body') }}</p>
        <AppButton ref="leaveButton" size="large" block @click="leave">
          {{ t('item.no_trip.action') }}
        </AppButton>
      </template>
    </template>
  </BottomSheet>
</template>

<script lang="ts">
import { computed, defineComponent, nextTick, onMounted, onUnmounted, ref, useId, watch } from 'vue'
import type { PropType } from 'vue'
import { useI18n } from 'vue-i18n'
import IconArrowDown from '~icons/mdi/arrow-down'
import IconBarcodeScan from '~icons/mdi/barcode-scan'
import IconCheck from '~icons/mdi/check'
import IconEqual from '~icons/mdi/equal'
import IconSwapHorizontal from '~icons/mdi/swap-horizontal'
import { formatEstimate, formatUnitPrice } from '@molvia/model'
import type {
  BaseUnit,
  CatalogueEntry,
  Currency,
  Money,
  TripExpenseView,
  TripView,
} from '@molvia/model'
import { api } from '@/api'
import AppButton from '@/components/AppButton.vue'
import AppField from '@/components/AppField.vue'
import CurrencySelect from '@/components/CurrencySelect.vue'
import AppReveal from '@/components/AppReveal.vue'
import BottomSheet from '@/components/BottomSheet.vue'
import SegmentedControl from '@/components/SegmentedControl.vue'
import { useAnnouncer } from '@/composables/useAnnouncer'
import { useTripHistoryStore } from '@/stores/tripHistory'
import { formatRating, unitPriceText } from '@/components/adviceRow'
import { useCheaperHint } from '@/composables/useCheaperHint'
import type { HintRecord } from '@/composables/useCheaperHint'
import { useCurrentTrip } from '@/composables/useCurrentTrip'
import { useFieldRefusal } from '@/composables/useFieldRefusal'
import { useItemDetails } from '@/composables/useItemDetails'
import { keyedIn } from '@/composables/useKeyboardInset'
import type { DetailsField, RetryPurchase } from '@/composables/useItemDetails'
import { useLocalDay } from '@/composables/useLocalDay'
import { calendarDayOfAnyYear } from '@/days'
import { useRecentItemsStore } from '@/stores/recentItems'
import { useTripStore } from '@/stores/trip'
import { useTripQueueStore } from '@/stores/tripQueue'

const UNITS: readonly BaseUnit[] = ['kg', 'l', 'piece']

/**
 * «Сколько, в чём, почём» for one item: the sheet every purchase goes through (MOL-24).
 *
 * It opens itself when mounted — the screen mounts it on a pick and unmounts it from `onClosed` —
 * so each opening has its own state and its own purchase identifier.
 *
 * Nothing is required but the item: the main action is always live, and blank fields mean «later».
 * What was typed and is not a number is not sent without it (В-5). A write goes to the trip queue
 * and the sheet closes at once — with a connection or without one, the same path (В-2).
 *
 * `closeSteps` is how many layers the write puts away: two over the search — the sheet and the
 * screen under it, back to the trip — one over the trip itself.
 */
export default defineComponent({
  name: 'ItemDetailsSheet',
  components: {
    AppButton,
    AppField,
    CurrencySelect,
    AppReveal,
    BottomSheet,
    IconBarcodeScan,
    IconSwapHorizontal,
    SegmentedControl,
  },
  props: {
    entry: { type: Object as PropType<CatalogueEntry>, required: true },
    /** What was typed before the item was picked; it goes with the purchase (MOL-11). */
    query: { type: String as PropType<string | null>, default: null },
    /** The query that found nothing on the search screen before this pick (MOL-45). */
    missedQuery: { type: String as PropType<string | null>, default: null },
    /** The row being amended; none when a purchase is being added. */
    expense: { type: Object as PropType<TripExpenseView | null>, default: null },
    /** A purchase the server refused, opened to be corrected and sent again (MOL-22, В-3). */
    retry: { type: Object as PropType<RetryPurchase | null>, default: null },
    /**
     * The trip this write belongs to, when it is not simply the one going on: a row opened from
     * «Поход» belongs to the trip it is in, which may have been finished elsewhere while the sheet
     * was up (MOL-24, С-3; MOL-22, review 8).
     */
    tripId: { type: String as PropType<string | null>, default: null },
    tripContext: { type: Object as PropType<TripView | null | undefined>, default: undefined },
    tripCurrency: { type: String as PropType<Currency | undefined>, default: undefined },
    closeSteps: { type: Number as PropType<1 | 2>, default: 1 },
    onClosed: { type: Function as PropType<() => void>, default: undefined },
    /** The code the item was found by or just given on the search (MOL-100); none otherwise. */
    code: { type: String as PropType<string | null>, default: null },
    /**
     * The sheet opens in the tap that asked for it, so «Сколько» takes the focus and iOS raises the
     * keys (MOL-212, Е-12). Opened by no tap — after the scanner, after a server's answer — the keys
     * stay down, and the field focused would have the sheet take their remembered height and fall
     * back 1.5 s later (MOL-151): × keeps the focus there, and where there is no record to write to.
     */
    focusQuantity: { type: Boolean, default: false },
  },
  emits: {
    added: (entry: CatalogueEntry) => typeof entry === 'object',
    saved: () => true,
    removed: () => true,
    /** The code was let go of by the item — «не этот товар?» (MOL-100). */
    detached: (code: string) => typeof code === 'string',
  },
  setup(props, { emit }) {
    const { t, locale } = useI18n()
    const trips = useTripStore()
    const queue = useTripQueueStore()

    const open = ref(true)
    const form = ref<HTMLElement | null>(null)
    // Both places that know whether a trip is going on: without the queue the sheet would say
    // «start a trip first» at a shelf where one was started with no signal (MOL-22, Р-2).
    const current = useCurrentTrip()
    const history = useTripHistoryStore()
    const localContext = computed(() => history.local.find((row) => row.id === props.tripId))
    const trip = computed(() =>
      props.tripContext !== undefined
        ? props.tripContext
        : props.tripId && props.tripId !== current.tripId.value
          ? history.known(props.tripId)
          : current.trip.value,
    )
    const currency = computed(
      () =>
        trip.value?.currency ??
        props.tripCurrency ??
        localContext.value?.currency ??
        current.currency.value,
    )
    // The trip the caller named, or the one going on — the search and a first purchase name none.
    const writeInto = computed(() =>
      props.tripId
        ? trip.value || localContext.value || props.retry
          ? props.tripId
          : null
        : current.tripId.value,
    )
    const editing = computed(() => props.expense !== null)
    /**
     * «Удалить позицию» is for anything already written down, whether the server has heard of it
     * or not: the wrong thing picked up at a shelf with no signal is undone by dropping the write,
     * not by sending it first and deleting the row it becomes (adversarial В2).
     */
    const removable = computed(() => props.expense !== null || props.retry !== null)

    /**
     * The trip's prices and what is still queued for it: a price must fit beside both (A9). The
     * purchase of this sheet is not counted — once queued it would be counted twice. The prices,
     * not the total: with a receipt's sum the total is the receipt (MOL-78), and the prices are
     * what the server adds this one to.
     */
    function occupied(): Money[] {
      const id = writeInto.value
      if (!id) return []
      const shown = trip.value
      const held: Money[] = [...(shown?.receipt ? shown.prices : (shown?.total ?? []))]
      for (const write of queue.pending) {
        if (write.kind !== 'add' || write.tripId !== id || !write.body.amount) continue
        if (write.body.id === details.expenseId) continue
        const amount = write.body.amount
        const index = held.findIndex((money) => money.currency === amount.currency)
        if (index === -1) held.push(amount)
        else {
          const sum = (held[index]?.minor ?? 0n) + amount.minor
          held[index] = { minor: sum, currency: amount.currency }
        }
      }
      return held
    }

    const details = useItemDetails({
      entry: props.entry,
      trip: () => trip.value,
      occupied,
      // A trip the server has not answered yet has no rate and no total, so the price starts in
      // the person's own currency — the one the server will give the trip anyway.
      currency: currency.value,
      expense: props.expense,
      retry: props.retry,
      separator: locale.value === 'ru' ? ',' : '.',
    })

    const units = computed(() => UNITS.map((value) => ({ value, label: t(`item.unit_${value}`) })))

    const perUnit = computed(() => {
      const price = details.unitPrice.value
      if (!price) return null
      return t('item.unit_price_value', {
        amount: formatUnitPrice(price, locale.value),
        unit: t(`item.unit_${price.unit}`),
      })
    })
    const converted = computed(() => {
      const money = details.converted.value
      return money ? formatEstimate(money, locale.value) : null
    })

    /**
     * «Тут дешевле» (MOL-92): the record written into, by its id once the server holds it — the
     * server reads its city off its place — and by the city of its start while that start is still
     * in the queue (review №1). No record, no hint.
     */
    const record = computed<HintRecord | null>(() => {
      const local = current.local.value
      const into = writeInto.value
      if (local?.id === into) {
        const queued = local.context
        return queued ? { where: { country: queued.country, city: queued.city } } : null
      }
      return into === null ? null : { tripId: into }
    })
    const { hint } = useCheaperHint({
      itemId: props.entry.id,
      record,
      except: props.expense?.id ?? null,
      here: () => trip.value?.place.id ?? null,
      typed: () => details.unitPrice.value,
      typedQuantity: () => details.typedQuantity.value,
      currency: () => details.currency.value,
      unit: () => details.unit.value,
    })
    // One's own history goes back across years, and a sheet has no month's caption over it (MOL-329, Ж1).
    const today = useLocalDay()
    const shortDay = (day: string) =>
      calendarDayOfAnyYear(day, locale.value, today.value, { day: '2-digit', month: '2-digit' })
    const HINT_ICONS = {
      best: IconArrowDown,
      there: IconArrowDown,
      same: IconEqual,
      cheaper: IconCheck,
    }
    const itemHint = computed(() => {
      const item = hint.value.item
      if (!item) return null
      const named = {
        place: item.place.name,
        price: unitPriceText(item.place.unitPrice, t, locale.value),
        day: shortDay(item.place.day),
      }
      return {
        kind: item.kind,
        icon: HINT_ICONS[item.kind],
        text: t(`item.cheaper.${item.kind}${item.here ? '_here' : ''}`, named),
      }
    })
    const alternativeHint = computed(() => {
      const other = hint.value.alternative
      if (!other) return null
      return t('item.cheaper.alternative', {
        name: other.name,
        price: unitPriceText(other.place.unitPrice, t, locale.value),
        place: other.place.name,
        day: shortDay(other.place.day),
        rating: formatRating(other.rating, locale.value),
      })
    })

    // Read out through the app's one live region, not a region of its own born with the sheet —
    // those are often not read (CLAUDE.md, MOL-19). Once typing pauses, not on every keystroke;
    // «Тут дешевле» with the figure it compares (Т-8).
    const spoken = computed(() =>
      [
        perUnit.value ? t('item.unit_price_announced', { value: perUnit.value }) : null,
        itemHint.value?.text ?? null,
        alternativeHint.value,
      ]
        .filter((line) => line !== null)
        .join('. '),
    )
    const announce = useAnnouncer()
    let withdraw: (() => void) | undefined
    let pause: ReturnType<typeof setTimeout> | undefined
    watch(spoken, (value) => {
      clearTimeout(pause)
      pause = setTimeout(() => {
        withdraw?.()
        withdraw = value ? announce?.(value) : undefined
      }, 700)
    })
    onUnmounted(() => {
      clearTimeout(pause)
      withdraw?.()
    })

    // Read now and on every change, not narrowed from a check made before: whether the note shows
    // is a statement about this moment.
    const online = ref(navigator.onLine)
    const listen = () => {
      online.value = navigator.onLine
    }
    onMounted(() => {
      // The dialog has already given «Сколько» the focus (`focusQuantity`). Filled — a purchase amended,
      // a refusal put right, the size of a pack — it is selected whole: the caret after «1» made «2»
      // typed at the shelf 12 l, and the price per litre a twelfth (adversarial А2).
      const quantity = form.value?.querySelector<HTMLInputElement>('[data-field="quantity"]')
      // A range set, not `select()`: WebKit on iOS has been unreliable with the latter (adversarial Р2).
      if (quantity && document.activeElement === quantity) {
        quantity.setSelectionRange(0, quantity.value.length)
      }
      window.addEventListener('online', listen)
      window.addEventListener('offline', listen)
      // The server is asked every time the sheet opens: the trip in memory is for when it cannot
      // be asked, not instead of asking — finished or started anew on another device, it would
      // otherwise take purchases for ever (review Р-2). A failure keeps the memory (В-6).
      //
      // Which trip to ask about is decided by the caller, not by comparing identifiers: only the
      // finished-trip screen hands the sheet a trip of its own, and a refusal being corrected on
      // «Поход» names the trip that refused it — an old one. Asked through the history that put
      // the selected trip out and skipped the check this branch exists for (А5).
      if (props.tripId && props.tripContext !== undefined) {
        void history.open(props.tripId).catch(() => undefined)
      } else {
        void trips.load().catch(() => undefined)
      }
    })
    onUnmounted(() => {
      window.removeEventListener('online', listen)
      window.removeEventListener('offline', listen)
    })

    // The record went while the sheet was up — finished on another device, the server says so as the
    // sheet asks: a field in focus would keep the keys up over a sheet with nothing to write, so the
    // focus goes to «К покупкам», the one thing left to do (adversarial А3). Only a field the keys are up
    // for, by the sheet's own rule (`keyedIn`): a button, a unit's radio and the currency's picker bring
    // none, and «Отменить» of «Отвязать?» is where the focus must wait (adversarial Р2-А1, Р3-А1, Р4-А1).
    // And the footer swaps whole as the record goes or comes: a button of it in focus goes with it, and
    // the focus fell to the body, outside the modal sheet (adversarial Р5-А1, Р6-А1, a fault master had
    // too) — so it is handed to the main action of the footer that comes. Read before the swap (`pre`).
    const leaveButton = ref<{ $el?: HTMLElement } | null>(null)
    const saveButton = ref<{ $el?: HTMLElement } | null>(null)
    const removeButton = ref<{ $el?: HTMLElement } | null>(null)
    watch(writeInto, async (into, was) => {
      if ((into === null) === (was === null)) return
      const held = document.activeElement
      const going = (into === null ? [saveButton, removeButton] : [leaveButton]).some(
        (button) => button.value?.$el !== undefined && button.value.$el === held,
      )
      const keyed = into === null && form.value !== null && keyedIn(form.value) !== null
      if (!going && !keyed) return
      await nextTick()
      ;(into === null ? leaveButton : saveButton).value?.$el?.focus()
    })

    let done = false
    const sheet = ref<{ close: (steps?: number) => void } | null>(null)

    function close(steps: number): void {
      // `close(2)` is the sheet's own: the screen under it goes along in the same step back, and
      // the sheet tells `open` itself once the pop lands.
      if (steps > 1 && sheet.value) sheet.value.close(steps)
      else open.value = false
    }

    // A field refused is heard (MOL-235, adversarial Р4-А1): «57о» in the price with the focus in it — a tap
    // on «Записать» Safari does not focus, or Enter — moved no focus and said nothing.
    const refuse = useFieldRefusal()
    function focus(field: DetailsField): void {
      void refuse(() => form.value?.querySelector<HTMLElement>(`[data-field="${field}"]`))
    }

    function submit(): void {
      if (done || !writeInto.value) return
      const wrong = details.validate()
      if (wrong) {
        focus(wrong)
        return
      }
      done = true

      if (props.expense) {
        const patch = details.patch()
        if (patch) {
          queue.enqueue({
            kind: 'update',
            tripId: writeInto.value,
            expenseId: props.expense.id,
            patch,
          })
          emit('saved')
        }
        close(props.closeSteps)
        return
      }

      queue.enqueue({
        kind: 'add',
        tripId: writeInto.value,
        body: details.body(
          props.retry?.query ?? props.query,
          props.retry?.missedQuery ?? props.missedQuery,
        ),
        entry: props.entry,
      })
      emit('added', props.entry)
      close(props.closeSteps)
    }

    /**
     * «Удалить позицию», wherever the purchase has got to by now. A row the server answered is
     * deleted through the queue; one still waiting is taken out of it and never sent. In between
     * there are two moments the sheet cannot see from its props (Т-3, Т-4): the purchase was
     * refused while the sheet was open — then dropping it takes the refusal off the screen too —
     * or it left and became a row — and then it has to be deleted like any other, or the sheet
     * would close on a purchase that is still in the trip.
     */
    function remove(): void {
      const purchase = props.expense?.id ?? props.retry?.id
      if (done || !writeInto.value || !purchase) return
      done = true
      const undone = !props.expense && queue.dropPurchase(writeInto.value, purchase)
      if (!undone) {
        queue.enqueue({ kind: 'remove', tripId: writeInto.value, expenseId: purchase })
      }
      emit('removed')
      close(props.closeSteps)
    }

    function leave(): void {
      close(props.closeSteps)
    }

    const recent = useRecentItemsStore()
    const detachQuestionId = `${useId()}-detach`
    const confirming = ref(false)
    const detachCancel = ref<{ $el?: HTMLElement } | null>(null)
    const detaching = ref(false)
    const detachFailed = ref(false)

    async function askDetach(): Promise<void> {
      confirming.value = true
      detachFailed.value = false
      await nextTick()
      detachCancel.value?.$el?.focus()
    }

    // The question goes with the button that held the focus: it comes back to the line that asked,
    // or it would fall to the body, outside the modal sheet (adversarial О).
    const notThisButton = ref<{ $el?: HTMLElement } | null>(null)
    async function keepCode(): Promise<void> {
      if (detaching.value) return
      confirming.value = false
      detachFailed.value = false
      await nextTick()
      notThisButton.value?.$el?.focus()
    }

    // One step back, to the search under the sheet: the code is to be given to its item there.
    async function notThis(): Promise<void> {
      const code = props.code
      if (done || detaching.value || code === null) return
      detaching.value = true
      detachFailed.value = false
      try {
        await api.detachBarcode(props.entry.id, code)
        // Here and not in the screen: a sheet put away while the answer was on its way emits to
        // nobody, and the device would go on finding the item by a code it no longer holds.
        recent.forgetCode(code)
        done = true
        emit('detached', code)
        close(1)
      } catch {
        detachFailed.value = true
      } finally {
        detaching.value = false
      }
    }

    return {
      t,
      open,
      form,
      sheet,
      quantity: details.quantity,
      unit: details.unit,
      amount: details.amount,
      currency: details.currency,
      errors: details.errors,
      leaveField: details.leave,
      units,
      perUnit,
      itemHint,
      alternativeHint,
      converted,
      online,
      writeInto,
      editing,
      removable,
      submit,
      remove,
      leave,
      leaveButton,
      saveButton,
      removeButton,
      confirming,
      detachQuestionId,
      detachCancel,
      askDetach,
      keepCode,
      notThisButton,
      detaching,
      detachFailed,
      notThis,
    }
  },
})
</script>

<style scoped lang="scss">
.form {
  display: grid;
  gap: var(--space-4);
}

.row {
  display: grid;
  grid-template-columns: minmax(0, 1fr) minmax(0, 1.3fr);
  gap: var(--space-3);
  align-items: start;
}

.per-unit-box {
  overflow: hidden;
  border-radius: var(--radius);
  background: var(--good-tint);
  color: var(--good-ink);
}

.per-unit {
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: var(--space-3);
  min-height: var(--touch-target-lg);
  padding: var(--space-3) var(--space-4);
}

/* One line under the figure, its tone by what it says: never red — red is «не брать нигде». */
.hint {
  display: flex;
  align-items: flex-start;
  gap: var(--space-2);
  margin: 0;
  padding: var(--space-2) var(--space-4) var(--space-3);
  border-top: var(--hairline) solid var(--border);
  font-size: var(--text-footnote);
  line-height: var(--leading-snug);
  overflow-wrap: anywhere;
}

.hint-icon {
  @include icon;

  font-size: var(--icon-sm);
}

.hint-there {
  background: var(--warn-tint);
  color: var(--warn-ink);
}

.hint-best,
.hint-same,
.hint-alternative {
  background: var(--surface-2);
  color: var(--text);
}

.hint-same {
  color: var(--text-muted);
}

.per-unit-label {
  font-size: var(--text-footnote);
  font-weight: var(--weight-medium);
}

.per-unit-value {
  @include display-type;

  font-size: var(--text-title);
  font-variant-numeric: tabular-nums;
  white-space: nowrap;
}

.caption {
  margin: calc(var(--space-2) * -1) 0 0;
  color: var(--text-muted);
  font-size: var(--text-footnote);
}

.note {
  display: block;
}

.offline {
  margin: 0;
  text-align: center;
  font-size: var(--text-footnote);
}

.detach {
  @include appear;

  display: flex;
  flex-direction: column;
  gap: var(--space-2);
  padding: var(--space-4);
  border-radius: var(--radius);
  background: var(--surface-2);
}

.detach-actions {
  display: flex;
  flex-wrap: wrap;
  gap: var(--space-2);

  /* Halves where both fit, as 4h draws them: a share of nothing each, never narrower than the cell of
     the wider word — so where the two do not fit they go one under the other (review №5). */
  > * {
    flex: 1 1 0;
    min-width: max-content;
  }
}

.detach-question {
  margin: 0;
  font-weight: var(--weight-medium);
  overflow-wrap: anywhere;
}

.no-trip {
  margin: 0;
  font-size: var(--text-footnote);
}
</style>
